import React, { useEffect, useRef, useState, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";
import Dashboardheader from "../../components/dashboardheader/DashboardHeader";
import {
  Button,
  Select,
  Card,
  Alert,
  Row,
  Col,
  Typography,
  Tabs,
  Divider,
  Breadcrumb,
  Radio,
  Input,
  Modal,
  Table,
  Spin
} from "antd";
import {
  CameraOutlined,
  VideoCameraOutlined,
  LoginOutlined,
  LogoutOutlined,
} from "@ant-design/icons";
import TodaysLog from "./TodaysLog";

const { Option } = Select;
const { Text } = Typography;
const { TabPane } = Tabs;
const { TextArea } = Input;

/* ─────────────────────────────────────────
   Ant Design Spin Overlay Component
───────────────────────────────────────── */
const CircularLoader = ({ message = "Processing..." }) => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      backgroundColor: "rgba(0, 0, 0, 0.45)",
      backdropFilter: "blur(3px)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 99999,
    }}
  >
    <Spin
      size="large"
      styles={{
        indicator: { color: "#ffffff" },
      }}
    >
      <div style={{ width: 80, height: 80 }} />
    </Spin>

    <p
      style={{
        marginTop: 12,
        color: "#ffffff",
        fontSize: 14,
        fontWeight: 500,
        letterSpacing: "0.5px",
        opacity: 0.9,
      }}
    >
      {message}
    </p>
  </div>
);

/* ─────────────────────────────────────────
   Loader message helper
───────────────────────────────────────── */
const getLoaderMessage = (loadingStage) => {
  switch (loadingStage) {
    case "location": return "Fetching Location Please Wait!";
    case "camera": return "Accessing camera...";
    case "submitting": return "Submitting attendance...";
    case "leads": return "Fetching customer leads...";
    default: return "Processing...";
  }
};

export default function AttendanceCapture({ open, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const navigate = useNavigate();

  const [yesterdayMin, setYesterdayMin] = useState("");
  const [yesterdayMax, setYesterdayMax] = useState("");

  useEffect(() => {
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const year = yesterday.getFullYear();
    const month = String(yesterday.getMonth() + 1).padStart(2, "0");
    const day = String(yesterday.getDate()).padStart(2, "0");
    setYesterdayMin(`${year}-${month}-${day}T00:00`);
    setYesterdayMax(`${year}-${month}-${day}T23:59`);
  }, []);

  const [photoColor, setPhotoColor] = useState(false);
  const [locationCheck, setLocationCheck] = useState({ latitude: 0.0, longitude: 0.0 });
  const [workLocation, setWorkLocation] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState("");
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState(null);

  const { authData, backUrl, bssBackUrl, googlemapAPI, setAuthData } = useContext(AuthContext);

  const [showTodayModal, setShowTodayModal] = useState(false);
  const [isOnduty, setIsOnduty] = useState("");
  const [onDutyStatus, setIsOndutyStatus] = useState("");
  const [checkInData, setCheckInData] = useState(null);
  const [checkOutData, setCheckOutData] = useState(null);
  const [masterData, setMasterData] = useState(null);
  const [checkIn, setCheckIn] = useState({ check_in_location: "", check_in_photo: null });
  const [salesManagers, setSalesManagers] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isLive, setIsLive] = useState(true);
  const [isPast, setIsPast] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);

  const isSalesUser = !!authData?.employee?.sales_id;

  useEffect(() => {
    if (!isSalesUser) {
      setIsOnduty("general");
    }
  }, [isSalesUser]);

  /* ─────────────────────────────────────────────────────────────
     Axios Interceptor — Auto Refresh Token / Logout on expiry
  ───────────────────────────────────────────────────────────── */
  useEffect(() => {
    // Flag to prevent infinite refresh loops
    let isRefreshing = false;
    // Queue of failed requests to retry after token refresh
    let failedQueue = [];

    const processQueue = (error, token = null) => {
      failedQueue.forEach((prom) => {
        if (error) {
          prom.reject(error);
        } else {
          prom.resolve(token);
        }
      });
      failedQueue = [];
    };

    const interceptor = axios.interceptors.response.use(
      // Pass through successful responses unchanged
      (response) => response,

      async (error) => {
        const originalRequest = error.config;

        // Only handle 401 Unauthorized errors
        // _retry flag prevents infinite loops on the same request
        if (error.response?.status === 401 && !originalRequest._retry) {
          // If a refresh is already in progress, queue this request
          // and wait for the new token before retrying
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                originalRequest.headers["Authorization"] = `Bearer ${token}`;
                return axios(originalRequest);
              })
              .catch((err) => Promise.reject(err));
          }

          // Mark this request so it won't trigger another refresh cycle
          originalRequest._retry = true;
          isRefreshing = true;

          try {
            // Attempt to get a new access token using the stored refresh token
            const refreshToken = authData?.refresh;

            if (!refreshToken) {
              // No refresh token available — force logout immediately
              throw new Error("No refresh token available");
            }

            const refreshResponse = await axios.post(
              `${backUrl}/api/token/refresh/`,
              { refresh: refreshToken }
            );

            const newAccessToken = refreshResponse.data.access;

            // Persist the new access token in AuthContext so all future
            // requests automatically use the updated token
            setAuthData((prev) => ({
              ...prev,
              access: newAccessToken,
            }));

            // Also update localStorage/sessionStorage if your app uses it
            const stored = localStorage.getItem("authData");
            if (stored) {
              const parsed = JSON.parse(stored);
              parsed.access = newAccessToken;
              localStorage.setItem("authData", JSON.stringify(parsed));
            }

            // Update the Authorization header for the queued/failed request
            originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;

            // Resolve all queued requests with the new token
            processQueue(null, newAccessToken);

            // Retry the original failed request with the refreshed token
            return axios(originalRequest);
          } catch (refreshError) {
            // Refresh failed (token expired or invalid) — reject all queued
            // requests and redirect the user to the logout route
            processQueue(refreshError, null);

            toast.error("Session expired. Please log in again.");

            // Clear auth state
            setAuthData(null);
            localStorage.removeItem("authData");

            // Redirect to logout page
            navigate("/logout");

            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        }

        // For all other errors, just pass them through normally
        return Promise.reject(error);
      }
    );

    // Cleanup: eject the interceptor when this component unmounts
    // to avoid duplicate interceptors if the component re-mounts
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [authData, backUrl, navigate, setAuthData]);

  /* ── Fetch today's attendance ── */
  const fetchData = async () => {
    try {
      const res = await axios.get(`${backUrl}/api/hr/attendance/today`, {
        headers: { Authorization: `Bearer ${authData.access}` },
      });
      setMasterData(res.data);
      setCheckInData({
        check_in_time: res.data.first_check_in,
        status: res.data.status,
        photo: res.data.first_check_in_photo,
        location: res.data.check_in_location,
        latitude: res.data.check_in_latitude,
        longitude: res.data.check_in_longitude,
      });
      setCheckOutData({
        check_out_time: res.data.last_check_out,
        photo: res.data.last_check_out_photo,
        location: res.data.check_out_location,
        latitude: res.data.check_out_latitude,
        longitude: res.data.check_out_longitude,
      });
    } catch (error) {
      console.error("Error fetching attendance data:", error);
    }
  };

  /* ── Fetch work locations ── */
  const fetchWorkLocation = async () => {
    try {
      const res = await axios.get(`${backUrl}/api/emp/worklocation/`);
      setWorkLocation(res.data.results);
    } catch (err) {
      console.error("Error fetching work locations", err);
    }
  };

  /* ── Fetch sales managers if customer visit ── */
  useEffect(() => {
    if (isOnduty === "customer") {
      const fetchSalesManagers = async () => {
        try {
          setLoadingStage("leads");
          setLoading(true);
          const res = await axios.get(
            `${bssBackUrl}/leads/customer_leads/${authData?.employee?.sales_id}/`,
            { headers: { Authorization: `Bearer ${authData.access}` } }
          );
          setSalesManagers(res.data.results || res.data);
        } catch (err) {
          console.error("Error fetching sales managers:", err);
          toast.error("Failed to fetch sales managers");
        } finally {
          setLoading(false);
          setLoadingStage("");
        }
      };
      fetchSalesManagers();
    }
  }, [isOnduty]);

  useEffect(() => {
    fetchWorkLocation();
    fetchData();
  }, []);

  /* ── Camera logic ── */
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false,
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        await videoRef.current.play();
        setCameraReady(true);
      }
    } catch (err) {
      console.error("Camera error:", err);
      toast.error("Camera access denied or not available.");
    }
  };

  useEffect(() => {
    startCamera();
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        setCameraReady(false);
      }
    };
  }, []);

  /* ── Geolocation ── */
  const getLocation = async (options = {}) => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) return reject(new Error("Geolocation not supported"));

      const defaultOptions = { enableHighAccuracy: true, timeout: 10000, maximumAge: 10000 };

      const watchId = navigator.geolocation.watchPosition(
        async (pos) => {
          const { latitude, longitude, accuracy } = pos.coords;
          setLocationCheck({ latitude, longitude });
          if (accuracy <= 10) {
            navigator.geolocation.clearWatch(watchId);
            resolve({ lat: latitude, lng: longitude, accuracy, name: await getAreaName(latitude, longitude) });
          }
        },
        (err) => { navigator.geolocation.clearWatch(watchId); reject(err); },
        { ...defaultOptions, ...options }
      );

      setTimeout(() => {
        navigator.geolocation.clearWatch(watchId);
        navigator.geolocation.getCurrentPosition(
          async (pos) => {
            const { latitude, longitude, accuracy } = pos.coords;
            setLocationCheck({ latitude, longitude });
            resolve({ lat: latitude, lng: longitude, accuracy, name: await getAreaName(latitude, longitude) });
          },
          reject,
          defaultOptions
        );
      }, 35000);
    });
  };

  const getAreaName = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
      );
      const data = await response.json();
      if (!data.address) return null;
      const { suburb, neighbourhood, village, town, city, county } = data.address;

      console.log("Data :", data)
      return data.display_name;
    } catch (error) {
      console.error("Error fetching area name:", error);
      return null;
    }
  };

  /* ── Capture Photo ── */
  const handleCapturePhoto = () => {
    if (!cameraReady) {
      toast.error("Camera not ready. Please wait for camera to load.");
      return;
    }
    if (videoRef.current && canvasRef.current) {
      const ctx = canvasRef.current.getContext("2d");
      ctx.drawImage(videoRef.current, 0, 0, 320, 240);
      const dataUrl = canvasRef.current.toDataURL("image/png");
      setCapturedPhoto(dataUrl);
      toast.success("Photo captured, Please proceed with Check-In and Check-Out");
    }
  };

  const base64ToFile = (base64String, fileName) => {
    const arr = base64String.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], fileName, { type: mime });
  };

  const getDistanceInMeters = (lat2, lon2, lat1, lon1) => {
    const R = 6371e3;
    const toRad = (deg) => (deg * Math.PI) / 180;
    const φ1 = toRad(lat1), φ2 = toRad(lat2);
    const Δφ = toRad(lat2 - lat1), Δλ = toRad(lon2 - lon1);
    const a =
      Math.sin(Δφ / 2) ** 2 +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  /* ── Attendance submit ── */
  const handleAttendance = async (type) => {
    if (!cameraReady) {
      toast.error("Camera not ready. Please wait for camera to load.");
      return;
    }
    if (!capturedPhoto) {
      toast.error("Please capture a photo before submitting attendance.");
      return;
    }
    if (!checkIn.check_in_location) {
      toast.error("Please select a work location");
      return;
    }
    const loc = workLocation.find((item) => item.name === checkIn.check_in_location);
    if (!loc) {
      toast.error("Work location not found");
      return;
    }
    if (checkIn.check_in_location?.toLowerCase().trim() === "on-duty" && !isOnduty) {
      toast.error("Please select an On-Duty type (General/Customer Visit)");
      return;
    }
    if (isOnduty && !isLive && !isPast) {
      toast.error("Please select Live or Past mode for OnDuty attendance.");
      return;
    }
    if (isPast && (!startTime || !endTime)) {
      toast.error("Please enter both start time and end time for past attendance.");
      return;
    }
    if (isOnduty === "customer" && (!selectedLead || !selectedLead.lead_no)) {
      toast.error("Please search and select a customer lead");
      return;
    }

    const image = capturedPhoto;

    try {
      setLoadingStage("location");
      setLoading(true);

      let coords = {};
      try {
        coords = await getLocation();
      } catch (err) {
        console.warn("Geolocation not available, continuing without coordinates");
        coords = {};
      }

      const userLat = parseFloat(coords.lat);
      const userLng = parseFloat(coords.lng);
      const userLocationName = coords.name;
      const workLat = parseFloat(loc.latitude);
      const workLng = parseFloat(loc.longitude);
      const radius = parseFloat(loc.radius);
      const distance = getDistanceInMeters(workLat, workLng, userLat, userLng);

      if (distance > radius) {
        toast.error(`You are ${Math.round(distance)} meters away. Must be within ${radius}m.`);
        setLoading(false);
        setLoadingStage("");
        return;
      }

      setLoadingStage("submitting");

      const formData = new FormData();
      const file = base64ToFile(image, "attendance.png");

      if (type === "Check-In") {
        formData.append("check_in_location", coords.name || "");
        formData.append("check_in_work_site", checkIn.check_in_location || "");
        formData.append("check_in_photo", file);
        formData.append("check_in_latitude", coords.lat);
        formData.append("check_in_longitude", coords.lng);
        formData.append("check_in_mode", isLive ? "LIVE" : "PAST");
        if (isPast) {
          formData.append("start_time", startTime);
          formData.append("end_time", endTime);
        }
        if (checkIn.check_in_location?.toLowerCase().trim() === "on-duty" && isOnduty) {
          formData.append("onduty_type_check_in", isOnduty);
          formData.append("check_in_remarks", onDutyStatus || "");
        }
        if (isOnduty === "customer" && selectedLead)
          formData.append("lead_data", JSON.stringify(selectedLead));

        console.log(formData)

        const res = await axios.post(`${backUrl}/api/hr/attendance/check-in/`, formData, {
          headers: { Authorization: `Bearer ${authData.access}` },
        });

        toast.success(res.data.message || "Check-In successful");
        setCheckInData({ ...res.data, photo: image, location: userLocationName });

      } else {
        if (userLocationName !== "" || null)
          formData.append("check_out_location", userLocationName || "");
        formData.append("check_out_work_site", checkIn.check_in_location || "");
        formData.append("check_out_photo", file);
        formData.append("check_out_latitude", coords.lat);
        formData.append("check_out_longitude", coords.lng);
        formData.append("check_out_mode", isLive ? "LIVE" : "PAST");
        if (isPast) {
          formData.append("start_time", startTime);
          formData.append("end_time", endTime);
        }
        if (checkIn.check_in_location?.toLowerCase().trim() === "on-duty" && isOnduty) {
          formData.append("onduty_type_check_out", isOnduty);
          formData.append("check_out_remarks", onDutyStatus || "");
        }
        if (isOnduty === "customer" && selectedLead)
          formData.append("lead_data", JSON.stringify(selectedLead));

        const res = await axios.post(`${backUrl}/api/hr/attendance/check-out/`, formData, {
          headers: { Authorization: `Bearer ${authData.access}` },
        });

        toast.success(res.data.message || "Check-Out successful");
        setCheckOutData({ ...res.data, photo: image, location: userLocationName });
      }
    } catch (err) {
      console.error("Error submitting attendance:", err);

      const data = err.response?.data;

      const backendMessage =
        data?.non_field_errors?.[0] ||
        data?.detail ||
        data?.error ||
        data?.message ||
        err.message ||
        "Failed to submit attendance.";

      toast.error(backendMessage);
    } finally {
      setLoading(false);
      setLoadingStage("");
    }
  };

  const activeStyle = { backgroundColor: "#0e497b", color: "white", borderColor: "#0e497b" };

  /* ────────────────────────────────────────── */
  return (
    <>
      {loading && <CircularLoader message={getLoaderMessage(loadingStage)} />}

      <Dashboardheader />
      <ToastContainer />

      <Breadcrumb style={{ margin: "16px 0 16px 20px", fontSize: 14, paddingLeft: "10px" }}>
        <Breadcrumb.Item
          onClick={() => navigate(-1)}
          style={{ color: "#0e497b", cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}
        >
          <ArrowLeftOutlined />
          <a id="bread-acr" style={{ color: "#0e497b" }}>Back</a>
        </Breadcrumb.Item>
        <Breadcrumb.Item onClick={() => setShowTodayModal(!showTodayModal)}>
          <a id="bread-acr" style={{ color: "#0e497b" }}>Attendance Log</a>
        </Breadcrumb.Item>
      </Breadcrumb>

      <Row gutter={[16, 16]} style={{ padding: "20px" }} justify={checkInData || checkOutData ? "start" : "center"}>
        <Col xs={24} sm={24} md={12} lg={12}>
          <Card bordered style={{ borderRadius: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
            {permissionDenied && (
              <Alert type="warning" showIcon message="Camera permission required" style={{ marginBottom: 16 }} />
            )}
            <div style={{ position: "relative", textAlign: "center", marginBottom: 16 }}>
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                width="100%"
                height="auto"
                style={{
                  borderRadius: 12,
                  border: cameraReady ? "1px solid #0e497b" : "1px solid #ccc",
                  backgroundColor: "#f0f0f0",
                  maxWidth: "300px",
                  display: capturedPhoto ? "none" : "block",
                  margin: "0 auto",
                }}
              />
              {!cameraReady && !capturedPhoto && (
                <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center" }}>
                  <CameraOutlined style={{ fontSize: 48, color: "#999" }} />
                  <p style={{ color: "#999", marginTop: 8 }}>Camera Off</p>
                </div>
              )}

              <canvas
                ref={canvasRef}
                width="320"
                height="240"
                style={{
                  display: capturedPhoto ? "block" : "none",
                  margin: "0 auto",
                  borderRadius: 12,
                  border: "2px solid #0e497b",
                  maxWidth: "300px",
                  width: "100%",
                }}
              />
            </div>

            {/* Work Location */}
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <Text strong style={{ display: "block", marginBottom: 8 }}>Work Location</Text>
              <Select
                showSearch
                allowClear
                value={checkIn.check_in_location}
                onChange={(value) =>
                  setCheckIn({ ...checkIn, check_in_location: value })
                }
                placeholder="Select place of work"
                style={{ width: "90%", maxWidth: 300 }}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.children
                    ?.toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {workLocation
                  ?.filter(loc => loc.name !== "Site Office")
                  .map((loc) => (
                    <Option key={loc.id} value={loc.name}>
                      {loc.name}
                    </Option>
                  ))}
              </Select>

              {checkIn.check_in_location?.toLowerCase().trim() !== "on-duty" ? (
                <Text type="danger" style={{ display: "block", marginTop: 8 }}>
                  {(() => {
                    const loc = workLocation.find(
                      (item) => item.name?.toLowerCase().trim() === checkIn.check_in_location?.toLowerCase().trim()
                    );
                    const radius = loc?.radius || 0;
                    if (checkIn.check_in_location === "") return null;
                    return `You must be within ${radius} meters of this location`;
                  })()}
                </Text>
              ) : (
                <>
                  <div className="mt-3">
                    <Radio.Group
                      onChange={(e) => setIsOnduty(e.target.value)}
                      value={isOnduty}
                      optionType="button"
                      buttonStyle="solid"
                      style={{
                        border: "none",
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "center",
                        gap: "8px",
                        marginTop: "10px",
                      }}
                    >
                      {(isSalesUser ? ["general", "customer"] : ["general"]).map((type) => (
                        <Radio.Button
                          key={type}
                          value={type}
                          style={{
                            backgroundColor: isOnduty === type ? "#0e497b" : "#fff",
                            color: isOnduty === type ? "#fff" : "#000",
                            borderColor: "#0e497b",
                          }}
                        >
                          {type === "general"
                            ? "General"
                            : type === "special"
                              ? "Special"
                              : "Customer Visit"}
                        </Radio.Button>
                      ))}
                    </Radio.Group>
                  </div>
                  <br />

                  <section className="d-flex justify-content-center">
                    <Button onClick={() => { setIsLive(true); setIsPast(false); }} style={isLive ? activeStyle : {}}>Live</Button>
                    <Button onClick={() => { setIsLive(false); setIsPast(true); }} className="ms-3" style={isPast ? activeStyle : {}}>Past</Button>
                  </section>

                  {isPast && (
                    <section className="d-flex justify-content-center gap-3 mt-3">
                      <div>
                        <label className="form-label">Start Time</label>
                        <input
                          type="datetime-local"
                          className="form-control"
                          value={startTime || ""}
                          min={yesterdayMin}
                          max={yesterdayMax}
                          onChange={(e) => setStartTime(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="form-label">End Time</label>
                        <input
                          type="datetime-local"
                          className="form-control"
                          value={endTime || ""}
                          min={yesterdayMin}
                          max={yesterdayMax}
                          onChange={(e) => setEndTime(e.target.value)}
                        />
                      </div>
                    </section>
                  )}

                  {isOnduty === "customer" && salesManagers?.length > 0 && (
                    <>
                      <div style={{ textAlign: "center", marginTop: 12 }}>
                        <Input
                          placeholder="Enter Lead No, or Phone"
                          style={{ maxWidth: "200px" }}
                          value={selectedLead?.searchText || ""}
                          onChange={(e) => {
                            const val = e.target.value.toLowerCase();
                            const filtered = salesManagers.find(
                              (sm) =>
                                sm.lead_no?.toString().toLowerCase().includes(val) ||
                                sm.number?.toString().toLowerCase().includes(val)
                            );
                            setSelectedLead(filtered ? { ...filtered, searchText: e.target.value } : { searchText: e.target.value });
                          }}
                        />
                      </div>

                      {selectedLead && selectedLead.lead_no && (
                        <div style={{ marginTop: 16 }}>
                          <Table
                            size="small"
                            pagination={false}
                            bordered
                            components={{
                              header: {
                                cell: (props) => (
                                  <th {...props} style={{ backgroundColor: "#0e497b", color: "white", marginTop: "20px" }}>
                                    {props.children}
                                  </th>
                                ),
                              },
                            }}
                            scroll={{ x: 1200 }}
                            columns={[
                              { title: "Lead No", dataIndex: "lead_no", key: "lead_no" },
                              { title: "Project", dataIndex: ["project", "title"], key: "title" },
                              { title: "Customer Name", dataIndex: "name", key: "name" },
                              {
                                title: "Phone", key: "number",
                                render: (record) => {
                                  const primary = record.number || "No primary phone number";
                                  const secondary = record.secondary_number || "9876543210";
                                  return secondary ? `${primary} / ${secondary}` : primary;
                                },
                              },
                              {
                                title: "Email", key: "email",
                                render: (record) => {
                                  const primary = record.email || "No primary email ID";
                                  const secondary = record.secondary_email || "samplemailid@gmail.com";
                                  return secondary ? `${primary} / ${secondary}` : primary;
                                },
                              },
                              { title: "Status", dataIndex: ["status", "title"], key: "status" },
                              {
                                title: "Assigned On", dataIndex: "assigned_on", key: "assigned_on",
                                render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
                              },
                            ]}
                            dataSource={[selectedLead]}
                            rowKey="lead_no"
                          />
                        </div>
                      )}
                    </>
                  )}

                  <TextArea
                    value={onDutyStatus}
                    onChange={(e) => setIsOndutyStatus(e.target.value)}
                    placeholder="Enter status"
                    rows={4}
                    style={{ width: "90%", maxWidth: 300, marginTop: 12 }}
                  />
                </>
              )}
            </div>
            <Divider />
            <div style={{ textAlign: "center", marginTop: "16px", marginBottom: "16px" }}>
              <Button
                type="primary"
                onClick={() => {
                  setPhotoColor(true);
                  handleCapturePhoto();
                }}
                disabled={!cameraReady}
                style={{ backgroundColor: photoColor ? "green" : "red", color: "white", borderRadius: 6, width: "100%", maxWidth: 300, border: photoColor ? "1px solid black" : "none" }}
                icon={<CameraOutlined />}
              >
                Capture Photo
              </Button>
            </div>

            <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: 10, alignItems: "center" }}>
              <Button
                type="primary"
                loading={loading && loadingStage === "submitting"}
                disabled={!cameraReady || !capturedPhoto || loading}
                onClick={() => handleAttendance("Check-In")}
                style={{ backgroundColor: "#0e497b", color: "white", borderRadius: 6, width: "90%", maxWidth: 300 }}
                icon={<LoginOutlined />}
              >
                Check-In
              </Button>

              {!isPast && (
                <Button
                  danger
                  loading={loading && loadingStage === "submitting"}
                  disabled={!cameraReady || !capturedPhoto || loading}
                  onClick={() => handleAttendance("Check-Out")}
                  icon={<LogoutOutlined />}
                  style={{ borderRadius: 6, width: "90%", maxWidth: 300 }}
                >
                  Check-Out
                </Button>
              )}
            </div>
          </Card>
        </Col>

        <Modal
          title="Attendance Log"
          open={showTodayModal}
          onCancel={() => setShowTodayModal(false)}
          width={"90%"}
          footer={[<Button key="close" onClick={() => setShowTodayModal(false)}>Close</Button>]}
        >
          <TodaysLog />
        </Modal>

        {(checkInData || checkOutData) && (
          <Col xs={24} sm={24} md={12} lg={12}>
            <Card bordered style={{ borderRadius: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
              <Tabs defaultActiveKey="1">
                {checkInData && (
                  <TabPane tab="Check-In" key="1">
                    <center>
                      <img
                        src={checkInData.photo}
                        alt="Check-In"
                        style={{ width: "100%", maxWidth: 320, borderRadius: 12, marginTop: 8, border: "1px solid #d9d9d9" }}
                      />
                      <div style={{ marginTop: 16, color: "#555" }}>
                        <p><b>Time:</b> {dayjs(checkInData.check_in_time).format("DD/MM/YYYY HH:mm:ss")}</p>
                        {/* {!checkOutData && (
                          <p>
                            <b>Status:</b>{" "}
                            <span style={{
                              color:
                                checkInData?.status === "Present" ? "green" :
                                  checkInData?.status === "Absent" ? "red" :
                                    checkInData?.status === "Late" ? "orange" : "gray",
                            }}>
                              {checkInData?.status || "Unknown"}
                            </span>
                          </p>
                        )} */}
                        {checkInData?.latitude && checkInData?.longitude && (
                          <p>
                            <b>Address:</b>{" "}
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${checkInData.latitude},${checkInData.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "#1677ff", textDecoration: "underline", cursor: "pointer" }}
                            >
                              {checkInData.location || "View on Google Maps"}
                            </a>
                          </p>
                        )}
                      </div>
                    </center>
                  </TabPane>
                )}

                {checkOutData && (
                  <TabPane tab="Check-Out" key="2">
                    {checkOutData.photo !== null ? (
                      <center>
                        <img
                          src={checkOutData.photo}
                          alt="Check-Out"
                          style={{ width: "100%", maxWidth: 320, borderRadius: 12, marginTop: 8, border: "1px solid #d9d9d9" }}
                        />
                        <div style={{ marginTop: 16, color: "#555" }}>
                          <p><b>Time:</b> {dayjs(checkOutData.check_out_time).format("DD/MM/YYYY HH:mm:ss")}</p>
                          <p>
                            <b>Address:</b>{" "}
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${checkOutData.latitude},${checkOutData.longitude}`}
                              rel="noopener noreferrer"
                              style={{ color: "#1677ff", textDecoration: "underline", cursor: "pointer" }}
                            >
                              {checkOutData.location}
                            </a>
                          </p>
                        </div>
                      </center>
                    ) : (
                      <center>No Checkout Data</center>
                    )}
                  </TabPane>
                )}
              </Tabs>
            </Card>
          </Col>
        )}
      </Row>
    </>
  );
}